import {type ImagePickerAsset, type ImagePickerResult} from 'expo-image-picker'

import {SUPPORTED_MIME_TYPES} from '#/lib/constants'

// mostly copied from expo-image-picker and adapted to support gifs
// also adds support for reading video metadata

export async function pickVideo(): Promise<ImagePickerResult> {
  const input = document.createElement('input')
  input.style.display = 'none'
  input.setAttribute('type', 'file')
  // TODO: do we need video/* here? -sfn
  input.setAttribute('accept', SUPPORTED_MIME_TYPES.join(','))
  input.setAttribute('id', String(Math.random()))

  document.body.appendChild(input)

  return new Promise(resolve => {
    input.addEventListener('change', async () => {
      if (input.files) {
        const file = input.files[0]
        resolve({
          canceled: false,
          assets: [await getVideoMetadata(file)],
        })
      } else {
        resolve({canceled: true, assets: null})
      }
      document.body.removeChild(input)
    })

    const event = new MouseEvent('click')
    input.dispatchEvent(event)
  })
}

// TODO: we're converting to a dataUrl here, and then converting back to an
// ArrayBuffer in the compressVideo function. This is a bit wasteful, but it
// lets us use the ImagePickerAsset type, which the rest of the code expects.
// We should unwind this and just pass the ArrayBuffer/objectUrl through the system
// instead of a string -sfn
export function getVideoMetadata(
  file: File | string,
): Promise<ImagePickerAsset> {
  if (typeof file === 'string')
    throw new Error(
      'getVideoMetadata was passed a uri, when on web it should be a File',
    )
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const uri = reader.result as string

      if (file.type === 'image/gif') {
        const img = new Image()
        img.onload = () => {
          resolve({
            uri,
            mimeType: 'image/gif',
            width: img.width,
            height: img.height,
            // todo: calculate gif duration. seems possible if you read the bytes
            // https://codepen.io/Ryman/pen/nZpYwY
            // for now let's just let the server reject it, since that seems uncommon -sfn
            duration: null,
          })
        }
        img.onerror = (_ev, _source, _lineno, _colno, error) => {
          console.log('Failed to grab GIF metadata', error)
          reject(new Error('Failed to grab GIF metadata'))
        }
        img.src = uri
      } else {
        const video = document.createElement('video')
        const blobUrl = URL.createObjectURL(file)

        video.preload = 'metadata'
        video.src = blobUrl

        // Set timeout for metadata loading (30 seconds)
        // Very long videos might take a while to load metadata
        const metadataTimeout = setTimeout(() => {
          URL.revokeObjectURL(blobUrl)
          console.warn('Video metadata loading timeout - using fallback values', {
            fileName: file.name,
            fileSize: file.size,
          })
          // For very long videos, metadata might not load in time
          // Use fallback values and let server validate
          resolve({
            uri,
            mimeType: file.type,
            width: 0, // Unknown - server will handle
            height: 0, // Unknown - server will handle
            duration: 400 * 60 * 1000, // Max allowed duration in ms - server will validate
          })
        }, 30000) // 30 second timeout

        video.onloadedmetadata = () => {
          clearTimeout(metadataTimeout)
          URL.revokeObjectURL(blobUrl)
          
          // Handle cases where duration might be NaN or Infinity for very long videos
          let duration = video.duration
          if (!isFinite(duration) || isNaN(duration)) {
            // For very long videos, browser might not be able to determine duration
            // Log warning but allow upload to proceed - server will validate
            console.warn('Video duration is not finite, using file size as fallback', {
              duration,
              fileSize: file.size,
              fileName: file.name,
            })
            // Set to a large value that will pass validation but server will handle
            // 400 minutes = 24,000,000 ms (our max)
            duration = 400 * 60 // seconds
          }
          
          resolve({
            uri,
            mimeType: file.type,
            width: video.videoWidth,
            height: video.videoHeight,
            // convert seconds to ms
            duration: duration * 1000,
          })
        }
        video.onerror = (_ev, _source, _lineno, _colno, error) => {
          clearTimeout(metadataTimeout)
          URL.revokeObjectURL(blobUrl)
          console.log('Failed to grab video metadata', error)
          // For very long videos, metadata might fail to load
          // Allow upload to proceed with fallback values
          console.warn('Video metadata error - using fallback values', {
            fileName: file.name,
            fileSize: file.size,
            error,
          })
          resolve({
            uri,
            mimeType: file.type,
            width: 0,
            height: 0,
            duration: 400 * 60 * 1000, // Max allowed - server will validate
          })
        }
      }
    }
    reader.readAsDataURL(file)
  })
}
